<template>
  <span class="tw:text-13 tw:text-muted">
    <!-- during the cooldown, only the countdown is shown -->
    <ResendCountdown
      v-if="cooldownActive"
      ref="countdownRef"
      :seconds="resendAfterSeconds"
      :busy="isSending"
      @resend="emit('send', 'sms')"
      @finished="emit('cooldownFinished')" />

    <template v-else>
      <template v-if="codeSent">
        {{ $t('registration.profile.mobile_didnt_get') }}
        <TextLinkButton :disabled="isSending" :data-testid="`${testidPrefix}-resend`" @click="emit('send', 'sms')">
          {{ $t('registration.profile.mobile_resend') }}
        </TextLinkButton>
      </template>
      <TextLinkButton
        v-else
        :disabled="isSending || isCalling"
        :data-testid="`${testidPrefix}-send`"
        @click="emit('send', 'sms')">
        {{ isSending ? $t('registration.profile.mobile_sending') : $t('registration.profile.mobile_send') }}
      </TextLinkButton>
      {{ ' ' }}{{ $t('registration.profile.mobile_or') }}{{ ' ' }}
      <TextLinkButton
        :disabled="isCalling || isSending"
        :data-testid="`${testidPrefix}-call`"
        @click="emit('send', 'call')">
        {{ isCalling ? $t('registration.profile.mobile_calling') : $t('registration.profile.mobile_call') }}
      </TextLinkButton>
    </template>

    <slot />
  </span>
</template>

<script setup lang="ts">
/** The send, resend and call actions under the code boxes, and the cooldown between requests. */
import { useTemplateRef } from 'vue';

import TextLinkButton from '@/components/generic/TextLinkButton.vue';
import ResendCountdown from '@/components/registration/subcomponents/ResendCountdown.vue';
import type { PhoneCodeChannel } from '@/types/types';

interface Props {
  testidPrefix: string;
  codeSent: boolean;
  cooldownActive: boolean;
  resendAfterSeconds: number;
  isSending: boolean;
  isCalling: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  send: [channel: PhoneCodeChannel];
  cooldownFinished: [];
}>();

const countdownRef = useTemplateRef<InstanceType<typeof ResendCountdown>>('countdownRef');

defineExpose({
  /** restarts the countdown; on the first request it is not mounted yet and picks the seconds up on mount */
  restart: (seconds: number) => countdownRef.value?.restart(seconds),
});
</script>
